import React, { useState } from "react";
import "./faq.css";
import { ScrollToTop } from "../../components";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  useFaqTitleSection,
  useFaqLinksSection,
  useFaqGroupsSection,
} from "../../hooks/faqHooks";

const FaqPage = () => {
  const {
    title,
    subtitle,
    loading: titleLoading,
    error: titleError,
  } = useFaqTitleSection();
  const {
    faqLinks,
    loading: linksLoading,
    error: linksError,
  } = useFaqLinksSection();
  const {
    faqGroups,
    loading: groupsLoading,
    error: groupsError,
  } = useFaqGroupsSection();

  const isMobile = useMediaQuery("(max-width: 600px)");

  const [selectedSection, setSelectedSection] = useState("general-immigration");
  const [query, setQuery] = useState("");

  const handleNavClick = (sectionID) => {
    setSelectedSection(sectionID);
    setQuery("");
  };

  const keys = ["title", "details"];
  const filterFaqsByQuery = (faqs) =>
    faqs?.filter((faq) =>
      keys.some((key) =>
        faq?.[key]?.toLowerCase().includes(query.toLowerCase())
      )
    ) || [];

  const allGroupsFiltered = (faqGroups || []).map((group) => {
    const matchedFaqs = filterFaqsByQuery(group?.faqs);
    return {
      ...group,
      faqs: matchedFaqs,
    };
  });

  let groupsToDisplay = [];

  if (isMobile) {
    // On mobile, show ALL groups, but only with their filtered FAQs
    groupsToDisplay = allGroupsFiltered.filter((g) => g?.faqs?.length > 0);
  } else {
    // On desktop, show only the selected group
    if (!query) {
      const selectedGroup =
        faqGroups?.find((g) =>
          g?.faqs?.some((faq) => faq?.sectionID === selectedSection)
        ) || faqGroups?.[0];

      if (selectedGroup) {
        const matchedFaqs = filterFaqsByQuery(selectedGroup.faqs);

        // Create an array with just this group if it has matching items
        if (matchedFaqs?.length > 0) {
          groupsToDisplay = [
            {
              ...selectedGroup,
              faqs: matchedFaqs,
            },
          ];
        }
      }
    } else {
      // if there's a query, show all groups with matching FAQs
      groupsToDisplay = allGroupsFiltered.filter((g) => g?.faqs?.length > 0);
    }
  }

  if (titleLoading || linksLoading || groupsLoading) {
    return (
      <section className="faq">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (titleError || linksError || groupsError) {
    return (
      <section className="faq">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="alert alert-danger" role="alert">
                Error: {titleError || linksError || groupsError}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="faq">
        <div className="container">
          <div className="faqNav">
            <div className="header">
              <h1>{title}</h1>
            </div>
            <p style={{ marginTop: "1rem" }}>{subtitle}</p>

            {!isMobile && (
              <div className="navList">
                <ul>
                  {faqLinks?.map((link) => (
                    <li key={link?.id}>
                      <button
                        onClick={() => handleNavClick(link?.id)}
                        className={
                          selectedSection === link?.id ? "selected" : ""
                        }
                      >
                        {link?.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="faqContent">
            <div className="searchBar">
              <input
                type="search"
                name="search"
                id="search"
                className="search"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {/* render the relevant groups */}
            {groupsToDisplay?.length === 0 ? (
              <p>No FAQs found for your search.</p>
            ) : (
              groupsToDisplay?.map((group) => {
                const { title, faqs } = group || {};
                if (!faqs?.length) {
                  return null;
                }

                const anchorID = faqs[0]?.sectionID;

                return (
                  <section
                    key={title}
                    id={anchorID}
                    style={{ marginBottom: "2rem" }}
                  >
                    <h2
                      className="group-title"
                      style={{ marginBottom: "1rem" }}
                    >
                      {title}
                    </h2>
                    {faqs.map((faq) => (
                      <div
                        className="mt-4"
                        key={faq?.id}
                        style={{ marginBottom: "1.3rem" }}
                      >
                        <h4 className="faq-title">{faq?.title}</h4>
                        <p>{faq?.details}</p>
                      </div>
                    ))}
                  </section>
                );
              })
            )}
          </div>
        </div>
      </section>
      <ScrollToTop />
    </>
  );
};

export default FaqPage;
